import type { ReactElement } from "react";

/**
 * Ícones filled dos tipos de entrega.
 *
 * O calendário, o filtro, o formulário e o portal usam o mesmo mapa. A chave é
 * o slug (`social_post`, `reels`…); nomes antigos de lucide (`megaphone`)
 * continuam resolvendo para não quebrar organizações já semeadas.
 */

export const TYPE_ICON_KEYS = [
  "social_post",
  "reels",
  "logo",
  "delivery",
  "content",
  "script",
  "shooting",
  "editing",
  "creative",
  "campaign",
  "meeting",
  "approval",
  "publication",
  "report",
  "admin",
  "milestone",
  "deadline",
  "other",
] as const;

export type TypeIconKey = (typeof TYPE_ICON_KEYS)[number];

const ALIASES: Record<string, TypeIconKey> = {
  megaphone: "social_post",
  "social-post": "social_post",
  post: "social_post",
  instagram: "social_post",
  film: "reels",
  video: "reels",
  reel: "reels",
  camera: "shooting",
  "pen-tool": "logo",
  sparkles: "logo",
  badge: "logo",
  stamp: "logo",
  package: "delivery",
  box: "delivery",
  truck: "delivery",
  "file-text": "content",
  file: "content",
  clapperboard: "script",
  scissors: "editing",
  palette: "creative",
  target: "campaign",
  users: "meeting",
  "check-circle": "approval",
  check: "approval",
  send: "publication",
  "bar-chart": "report",
  chart: "report",
  clipboard: "admin",
  flag: "milestone",
  "alarm-clock": "deadline",
  alarm: "deadline",
  circle: "other",
};

function normalizeKey(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");
}

export function resolveTypeIconKey(slugOrName: string | null | undefined): TypeIconKey {
  if (slugOrName === null || slugOrName === undefined || slugOrName === "") return "other";
  const key = normalizeKey(slugOrName);
  if ((TYPE_ICON_KEYS as readonly string[]).includes(key)) return key as TypeIconKey;
  const dashed = key.replace(/_/g, "-");
  return ALIASES[key] ?? ALIASES[dashed] ?? "other";
}

function Glyph({
  children,
  size,
  className,
}: {
  children: ReactElement | ReactElement[];
  size: number;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

const ICONS: Record<TypeIconKey, (size: number, className?: string) => ReactElement> = {
  social_post: (size, className) => (
    <Glyph size={size} className={className}>
      <path
        fill="currentColor"
        d="M3.6 8.2h4.7v7.6H3.6c-.9 0-1.6-.7-1.6-1.6v-4.4c0-.9.7-1.6 1.6-1.6Zm6.2-.7 7.2-5.2c.7-.5 1.7 0 1.7.9v17.6c0 .9-1 1.4-1.7.9l-7.2-5.2V7.5Zm13.5 2.3v4.2c0 .6-.4 1-1 1h-1.4v-6.2H22c.6 0 1.1.5 1.1 1Z"
      />
    </Glyph>
  ),
  reels: (size, className) => (
    <Glyph size={size} className={className}>
      <path
        fill="currentColor"
        d="M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4Zm1.1 2.2h1.5v2.2H8.1V5.2Zm5.3 0h1.5v2.2h-1.5V5.2Zm-5.3 11.4h1.5v2.2H8.1v-2.2Zm5.3 0h1.5v2.2h-1.5v-2.2ZM10.8 8.8 16 12l-5.2 3.2V8.8Z"
      />
    </Glyph>
  ),
  logo: (size, className) => (
    <Glyph size={size} className={className}>
      <path
        fill="currentColor"
        d="M12 2.2 20.4 7v10L12 21.8 3.6 17V7L12 2.2Zm0 4.1c-2.4 0-4.3 1.8-4.3 4.1 0 1.6.9 3 2.3 3.7l-1.4 3.2h6.8l-1.4-3.2c1.4-.7 2.3-2.1 2.3-3.7 0-2.3-1.9-4.1-4.3-4.1Zm0 2.2c1.1 0 2 .8 2 1.9S13.1 12.3 12 12.3s-2-.8-2-1.9.9-1.9 2-1.9Z"
      />
    </Glyph>
  ),
  delivery: (size, className) => (
    <Glyph size={size} className={className}>
      <path
        fill="currentColor"
        d="M3 7.2 12 3l9 4.2v9.6L12 21l-9-4.2V7.2Zm9 1.3 5.6-2.6L12 5.2 6.4 7.9 12 8.5Zm-7.2 1.4v6.3L11 19.3v-6.4L4.8 9.9Zm14.4 0L13 12.9v6.4l6.2-2.9V9.9Z"
      />
    </Glyph>
  ),
  content: (size, className) => (
    <Glyph size={size} className={className}>
      <path
        fill="currentColor"
        d="M6 2.5h8.2L20 8.3V20a1.5 1.5 0 0 1-1.5 1.5h-12A1.5 1.5 0 0 1 5 20V4a1.5 1.5 0 0 1 1-1.5Zm8.5 1.8V8h4.2l-4.2-3.7ZM8 11.2h8v1.6H8v-1.6Zm0 3.3h8v1.6H8v-1.6Zm0 3.3h5.5v1.6H8v-1.6Z"
      />
    </Glyph>
  ),
  script: (size, className) => (
    <Glyph size={size} className={className}>
      <path
        fill="currentColor"
        d="M4.2 5.2h15.6c.7 0 1.2.6 1.2 1.2v2.1H3V6.4c0-.7.5-1.2 1.2-1.2Zm.8-2.4h2.1l.7 2.4H5.7l-.7-2.4Zm5.4 0h2.2l.7 2.4H9.7l-.7-2.4Zm5.5 0h2.1l.7 2.4h-2.1l-.7-2.4ZM3 9.8h18V19a1.8 1.8 0 0 1-1.8 1.8H4.8A1.8 1.8 0 0 1 3 19V9.8Zm4.2 2.6v1.7h9.6v-1.7H7.2Zm0 3.4v1.7h6.4v-1.7H7.2Z"
      />
    </Glyph>
  ),
  shooting: (size, className) => (
    <Glyph size={size} className={className}>
      <path
        fill="currentColor"
        d="M8.2 5.2 9.4 3.4h5.2l1.2 1.8H19a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-11a2 2 0 0 1 2-2h3.2ZM12 9.2a4.3 4.3 0 1 0 0 8.6 4.3 4.3 0 0 0 0-8.6Zm0 1.8a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM17.6 8.4a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z"
      />
    </Glyph>
  ),
  editing: (size, className) => (
    <Glyph size={size} className={className}>
      <path
        fill="currentColor"
        d="M8.1 3.4a3.4 3.4 0 0 1 1.3 6.5L7.8 12l1.6 2.1A3.4 3.4 0 1 1 7.7 16L12 10.4 16.3 16a3.4 3.4 0 1 1-1.7-1.9L16.2 12l-1.6-2.1A3.4 3.4 0 1 1 17.9 8L12 15.6 6.1 8a3.4 3.4 0 0 1 2-4.6Zm0 1.8a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2Zm7.8 0a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2ZM6.5 17.1a1.6 1.6 0 1 0 2.7 1.7 1.6 1.6 0 0 0-2.7-1.7Zm8.3 0a1.6 1.6 0 1 0 2.7 1.7 1.6 1.6 0 0 0-2.7-1.7Z"
      />
    </Glyph>
  ),
  creative: (size, className) => (
    <Glyph size={size} className={className}>
      <path
        fill="currentColor"
        d="M12.2 2.4c4.8 0 8.6 3.4 8.6 7.1 0 2.6-1.8 4.4-4.1 4.4h-1.3c-.6 0-1 .5-1 1.1 0 .4.2.7.5 1 .6.6.9 1.4.9 2.2 0 1.9-1.6 3.4-3.6 3.4-4.7 0-8.8-3.8-8.8-8.6 0-5.1 3.9-10.6 8.8-10.6Zm-3.3 4.2a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6Zm3.6-.9a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6Zm3.4 1.6a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6ZM8.3 10.8a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6Z"
      />
    </Glyph>
  ),
  campaign: (size, className) => (
    <Glyph size={size} className={className}>
      <path
        fill="currentColor"
        d="M12 2.2a9.8 9.8 0 1 1 0 19.6 9.8 9.8 0 0 1 0-19.6Zm0 3.3a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13Zm0 3.2a3.3 3.3 0 1 1 0 6.6 3.3 3.3 0 0 1 0-6.6Zm0 2a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6Z"
      />
    </Glyph>
  ),
  meeting: (size, className) => (
    <Glyph size={size} className={className}>
      <path
        fill="currentColor"
        d="M9.2 3.6a3.6 3.6 0 1 1 0 7.2 3.6 3.6 0 0 1 0-7.2ZM2.4 16.4c0-2.8 2.6-5 6.8-5s6.8 2.2 6.8 5v1.2c0 .8-.6 1.4-1.4 1.4H3.8c-.8 0-1.4-.6-1.4-1.4v-1.2Zm13.3-6.6a3 3 0 1 1 0-6 3 3 0 0 1 0 6Zm.5 1.4c3.4.3 5.4 2.3 5.4 4.8v1.2c0 .8-.6 1.4-1.4 1.4h-3.3v-1.2c0-2.4-1.1-4.2-3.3-5.2 1-.6 1.8-1 2.6-1Z"
      />
    </Glyph>
  ),
  approval: (size, className) => (
    <Glyph size={size} className={className}>
      <path
        fill="currentColor"
        d="M12 2.2a9.8 9.8 0 1 1 0 19.6 9.8 9.8 0 0 1 0-19.6Zm4.2 6.3L10.6 14l-2.8-2.8-1.6 1.6 4.4 4.4 7.2-7.1-1.6-1.6Z"
      />
    </Glyph>
  ),
  publication: (size, className) => (
    <Glyph size={size} className={className}>
      <path
        fill="currentColor"
        d="M3.2 4.1c-.7-.4-.7-1.4.1-1.7L20.4 9c.8.3.8 1.4 0 1.7l-8.2 3.3-1.1 6.3c-.1.8-1.2 1-1.6.3L7.2 16.4 3.2 4.1Zm8.4 9.2 6.2-2.5-9.8-3.9 3.6 6.4Z"
      />
    </Glyph>
  ),
  report: (size, className) => (
    <Glyph size={size} className={className}>
      <path
        fill="currentColor"
        d="M4.2 3.2h15.6c.7 0 1.2.5 1.2 1.2v15.2c0 .7-.5 1.2-1.2 1.2H4.2c-.7 0-1.2-.5-1.2-1.2V4.4c0-.7.5-1.2 1.2-1.2Zm3 10.4v4.2h2.4v-4.2H7.2Zm4.1-5.2v9.4h2.4V8.4h-2.4Zm4.1 2.6v6.8h2.4v-6.8h-2.4Z"
      />
    </Glyph>
  ),
  admin: (size, className) => (
    <Glyph size={size} className={className}>
      <path
        fill="currentColor"
        d="M8.2 3.4h7.6v2.2H8.2V3.4Zm-2.6 1.5h2.2v2.3h8.4V4.9h2.2c.8 0 1.4.6 1.4 1.4V20c0 .8-.6 1.4-1.4 1.4H5.6c-.8 0-1.4-.6-1.4-1.4V6.3c0-.8.6-1.4 1.4-1.4Zm3.2 6.3v1.6h8.4v-1.6H8.8Zm0 3.4v1.6h8.4v-1.6H8.8Z"
      />
    </Glyph>
  ),
  milestone: (size, className) => (
    <Glyph size={size} className={className}>
      <path fill="currentColor" d="M6.4 2.4h1.9v19.2H6.4V2.4Zm2.6 1.2 10.4 4.4-10.4 4.6V3.6Z" />
    </Glyph>
  ),
  deadline: (size, className) => (
    <Glyph size={size} className={className}>
      <path
        fill="currentColor"
        d="M8.4 2.4h7.2v2.1H8.4V2.4ZM12 5.6a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4Zm.8 3.2h-1.7v5.1l3.8 2.3.9-1.4-3-1.8V8.8ZM4.3 6.2 2.8 7.7l1.8 1.8 1.5-1.5-1.8-1.8Zm15.4 0-1.8 1.8 1.5 1.5 1.8-1.8-1.5-1.5Z"
      />
    </Glyph>
  ),
  other: (size, className) => (
    <Glyph size={size} className={className}>
      <path fill="currentColor" d="M12 4.2a7.8 7.8 0 1 1 0 15.6 7.8 7.8 0 0 1 0-15.6Z" />
    </Glyph>
  ),
};

export interface TypeIconProps {
  slug?: string | null;
  name?: string | null;
  size?: number;
  className?: string;
}

export function TypeIcon({ slug, name, size = 16, className }: TypeIconProps) {
  const key = resolveTypeIconKey(slug ?? name);
  return ICONS[key](size, className);
}
